import { useState, useEffect } from 'react';
import { useAuth } from '@/components/AuthProvider';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase/config';

const ADMIN_EMAILS = ['jussi@concept.zone']; // Fallback for backward compatibility

export function useAdmin() {
    const { user } = useAuth();
    const [isAdmin, setIsAdmin] = useState(false);
    const [checkedUserId, setCheckedUserId] = useState<string | null>(null);

    // Loading is true if we haven't checked this user yet
    const loading = user ? user.uid !== checkedUserId : false;

    useEffect(() => {
        async function checkAdminStatus() {
            if (!user) {
                setIsAdmin(false);
                setCheckedUserId(null);
                return;
            }

            // If we've already checked this user, don't check again
            if (user.uid === checkedUserId) {
                return;
            }

            try {
                const userDoc = await getDoc(doc(db, 'users', user.uid));
                if (userDoc.exists()) {
                    const userData = userDoc.data();
                    // Check both isAdmin field and hardcoded email list for backward compatibility
                    const adminStatus = userData?.isAdmin === true ||
                        (user.email ? ADMIN_EMAILS.includes(user.email) : false);
                    setIsAdmin(adminStatus);
                } else {
                    // If no user doc, check hardcoded list
                    const adminStatus = user.email ? ADMIN_EMAILS.includes(user.email) : false;
                    setIsAdmin(adminStatus);
                }
            } catch (error) {
                console.error('[useAdmin] Error checking admin status:', error);
                // Fallback to hardcoded list on error
                const adminStatus = user.email ? ADMIN_EMAILS.includes(user.email) : false;
                setIsAdmin(adminStatus);
            } finally {
                // Mark this user as checked
                setCheckedUserId(user.uid);
            }
        }

        checkAdminStatus();
    }, [user, checkedUserId]);

    return {
        isAdmin,
        loading,
    };
}